import { useCallback, useEffect, useMemo, useState } from "react";
import {
  FiEdit2,
  FiPlus,
  FiSearch,
  FiTrash2,
  FiTruck,
} from "react-icons/fi";
import SupplierModal from "../../components/masters/SupplierModal";
import { useAuth } from "../../context/AuthContext";
import { apiRequest } from "../../services/api";
import ClientPagination, { useClientPagination } from "../../components/common/ClientPagination";

export default function SuppliersPage() {
  const { accessToken } = useAuth();

  const [suppliers, setSuppliers] = useState([]);
  const [search, setSearch] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingSupplier, setEditingSupplier] = useState(null);

  const loadSuppliers = useCallback(async () => {
    setIsLoading(true);
    setError("");

    try {
      const response = await apiRequest("/suppliers", { accessToken });
      setSuppliers(
        Array.isArray(response) ? response : response?.data || [],
      );
    } catch (requestError) {
      setError(requestError.message || "Unable to load suppliers.");
    } finally {
      setIsLoading(false);
    }
  }, [accessToken]);

  useEffect(() => {
    loadSuppliers();
  }, [loadSuppliers]);

  const filteredSuppliers = useMemo(() => {
    const query = search.trim().toLowerCase();

    if (!query) return suppliers;

    return suppliers.filter((supplier) =>
      [
        supplier.displayName,
        supplier.legalName,
        supplier.code,
        supplier.phone,
        ...(supplier.additionalPhones || []),
        supplier.email,
        supplier.city,
        supplier.gstin,
      ].some((value) => value?.toLowerCase().includes(query)),
    );
  }, [search, suppliers]);

  const stats = useMemo(() => {
    const active = suppliers.filter((supplier) => supplier.isActive).length;
    const gstRegistered = suppliers.filter((supplier) => supplier.gstin).length;

    return {
      total: suppliers.length,
      active,
      inactive: suppliers.length - active,
      gstRegistered,
    };
  }, [suppliers]);
  const suppliersPagination = useClientPagination(filteredSuppliers);

  const handleSave = async (form) => {
    setError("");

    try {
      if (editingSupplier) {
        await apiRequest(`/suppliers/${editingSupplier.id}`, {
          method: "PATCH",
          accessToken,
          body: form,
        });
      } else {
        const { isActive, ...createPayload } = form;

        await apiRequest("/suppliers", {
          method: "POST",
          accessToken,
          body: createPayload,
        });
      }

      setShowModal(false);
      setEditingSupplier(null);
      await loadSuppliers();
    } catch (requestError) {
      setError(requestError.message || "Unable to save supplier.");
    }
  };

  const handleDelete = async (supplier) => {
    if (
      !window.confirm(
        `Delete "${supplier.displayName}"? This will fail if products use this supplier.`,
      )
    ) {
      return;
    }

    try {
      setError("");
      await apiRequest(`/suppliers/${supplier.id}`, {
        method: "DELETE",
        accessToken,
      });
      await loadSuppliers();
    } catch (requestError) {
      setError(requestError.message || "Unable to delete supplier.");
    }
  };

  return (
    <div>
      <div className="mb-8 flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <p className="text-sm text-gray-500">Masters / Suppliers</p>
          <h1 className="mt-1 text-3xl font-bold text-gray-900">
            Suppliers Management
          </h1>
          <p className="mt-2 text-gray-500">
            Manage vendors and product suppliers.
          </p>
        </div>

        <button
          type="button"
          onClick={() => {
            setEditingSupplier(null);
            setShowModal(true);
          }}
          className="flex h-11 items-center justify-center gap-2 rounded-xl bg-primary-800 px-5 text-white hover:bg-primary-900"
        >
          <FiPlus />
          Add Supplier
        </button>
      </div>

      {error && (
        <div className="mb-6 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}

      <div className="mb-8 grid gap-5 md:grid-cols-4">
        {[
          ["Total Suppliers", stats.total, "text-gray-900"],
          ["Active", stats.active, "text-green-600"],
          ["Inactive", stats.inactive, "text-red-500"],
          ["GST Registered", stats.gstRegistered, "text-primary-800"],
        ].map(([label, value, color]) => (
          <div key={label} className="rounded-3xl border bg-white p-6">
            <p className="text-sm text-gray-500">{label}</p>
            <h2 className={`mt-2 text-3xl font-bold ${color}`}>{value}</h2>
          </div>
        ))}
      </div>

      <div className="overflow-hidden rounded-3xl border border-gray-100 bg-white">
        <div className="border-b border-gray-100 p-5">
          <div className="relative max-w-md">
            <FiSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search suppliers..."
              className="h-12 w-full rounded-xl border border-gray-200 pl-12 pr-4 outline-none focus:border-primary-700 focus:ring-2 focus:ring-primary-100"
            />
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full min-w-[950px]">
            <thead>
              <tr className="border-b bg-gray-50">
                {["Supplier", "Code", "Phone", "GSTIN", "City", "Status", "Actions"].map((heading) => (
                  <th
                    key={heading}
                    className={`px-6 py-4 text-xs uppercase text-gray-500 ${
                      heading === "Actions" ? "text-right" : "text-left"
                    }`}
                  >
                    {heading}
                  </th>
                ))}
              </tr>
            </thead>

            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan="7" className="px-6 py-12 text-center text-sm text-gray-500">
                    Loading suppliers...
                  </td>
                </tr>
              ) : filteredSuppliers.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-6 py-12 text-center text-sm text-gray-500">
                    No suppliers found.
                  </td>
                </tr>
              ) : (
                suppliersPagination.pageItems.map((supplier) => (
                  <tr key={supplier.id} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-4">
                        <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-primary-50 text-primary-700">
                          <FiTruck size={20} />
                        </div>
                        <div>
                          <p className="font-semibold text-gray-900">{supplier.displayName}</p>
                          {supplier.legalName && (
                            <p className="text-xs text-gray-500">{supplier.legalName}</p>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 font-mono text-sm">{supplier.code}</td>
                    <td className="px-6 py-4 text-sm">{[supplier.phone, ...(supplier.additionalPhones || [])].filter(Boolean).join(' · ') || "—"}</td>
                    <td className="px-6 py-4 text-sm">{supplier.gstin || "—"}</td>
                    <td className="px-6 py-4 text-sm">{supplier.city || "—"}</td>
                    <td className="px-6 py-4">
                      <span className={`rounded-full px-3 py-1 text-xs font-semibold ${
                        supplier.isActive
                          ? "bg-green-100 text-green-700"
                          : "bg-red-100 text-red-600"
                      }`}>
                        {supplier.isActive ? "Active" : "Inactive"}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex justify-end gap-2">
                        <button
                          type="button"
                          onClick={() => {
                            setEditingSupplier(supplier);
                            setShowModal(true);
                          }}
                          className="flex h-9 w-9 items-center justify-center rounded-lg border text-gray-600 hover:border-primary-300 hover:bg-primary-50 hover:text-primary-800"
                        >
                          <FiEdit2 />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDelete(supplier)}
                          className="flex h-9 w-9 items-center justify-center rounded-lg border text-red-500 hover:border-red-200 hover:bg-red-50"
                        >
                          <FiTrash2 />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <ClientPagination pagination={suppliersPagination} label="suppliers" />
      </div>

      <SupplierModal
        open={showModal}
        onClose={() => {
          setShowModal(false);
          setEditingSupplier(null);
        }}
        onSave={handleSave}
        initialData={editingSupplier}
      />
    </div>
  );
}
